import {
    Typography,
    Grid,
    Box,
    Paper,
    useTheme,
    useMediaQuery,
    Button,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { useState, useCallback } from "react";
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { LoadingButton } from "@mui/lab";
import Avatar from "react-avatar-edit";
import MyToastShow from "../Layout/MyToast";
import { convertBase64ToFile } from "@/utils/allUtils";

interface PhotoUpdateModalProps {
    open: boolean;
    onClose: () => void;
    onPhotoUpdate: (formData: FormData) => Promise<any>;
    isUpdating: boolean;
    userName: string;
}

const PhotoUpdateModal = ({ 
    open, 
    onClose, 
    onPhotoUpdate, 
    isUpdating, 
    userName 
}: PhotoUpdateModalProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [preview, setPreview] = useState<string | null>(null);

    const handleUpdatePhoto = useCallback(async () => {
        if (!preview) return;
        
        try {
            const file = convertBase64ToFile(preview, 'fotoPerfil.gif');
            const formData = new FormData();
            formData.append('foto', file);
            
            await onPhotoUpdate(formData);
            
            MyToastShow({ 
                message: 'Foto actualizada correctamente', 
                type: 'success' 
            });
            
            setPreview(null);
            onClose();
        } catch (error) {
            MyToastShow({ 
                message: 'Error al actualizar la foto', 
                type: 'error' 
            });
        }
    }, [preview, onPhotoUpdate, onClose]);

    const handleClose = () => {
        setPreview(null);
        onClose();
    };

    const handleFileValidation = (elem: any) => {
        if (elem.target.files[0].size > 2097152) { // 2MB
            MyToastShow({ 
                message: 'La imagen es demasiado grande. Máximo 2MB permitido.', 
                type: 'error' 
            });
            elem.target.value = "";
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: theme.palette.background.paper
                }
            }}
        >
            <DialogTitle 
                sx={{ 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white',
                    textAlign: 'center',
                    py: 2
                }}
            >
                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 45,
                            height: 45,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            mb: 0.5
                        }}
                    >
                        <PhotoCameraIcon fontSize="medium" />
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                        Actualizar foto de perfil
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                        Selecciona y recorta tu nueva imagen de perfil
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        {/* Editor Section */}
                        <Grid item xs={12} md={8}>
                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    border: `2px dashed ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    p: 2,
                                    textAlign: 'center',
                                    minHeight: 250,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    background: theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[900] 
                                        : theme.palette.grey[50]
                                }}
                            >
                                <Avatar
                                    width={isMobile ? 200 : 250}
                                    height={isMobile ? 200 : 250}
                                    label="Seleccionar imagen"
                                    onCrop={(preview) => setPreview(preview)}
                                    onClose={() => setPreview(null)}
                                    onBeforeFileLoad={handleFileValidation}
                                    borderStyle={{
                                        border: `3px solid ${theme.palette.primary.main}`,
                                        borderRadius: '8px'
                                    }}
                                    backgroundColor={theme.palette.mode === 'dark' 
                                        ? theme.palette.grey[800] 
                                        : theme.palette.background.paper
                                    }
                                />
                                {!preview && (
                                    <Box mt={1}>
                                        <Typography variant="body2" color="text.secondary">
                                            Arrastra una imagen aquí o haz clic para seleccionar
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            Formatos soportados: JPG, PNG, GIF (máx. 2MB)
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                        
                        {/* Preview Section */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ position: 'sticky', top: 0 }}>
                                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
                                    Vista previa
                                </Typography>
                                <Paper 
                                    elevation={3} 
                                    sx={{ 
                                        p: 2, 
                                        textAlign: 'center',
                                        borderRadius: 2,
                                        background: preview 
                                            ? theme.palette.background.paper
                                            : theme.palette.mode === 'dark' 
                                                ? theme.palette.grey[900] 
                                                : theme.palette.grey[50]
                                    }}
                                >
                                    {preview ? (
                                        <Box>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    {userName}
                                                </Typography>
                                                <img 
                                                    src={preview} 
                                                    alt="Preview" 
                                                    style={{ 
                                                        width: 100, 
                                                        height: 100, 
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                        border: `3px solid ${theme.palette.primary.main}`,
                                                        boxShadow: theme.shadows[4]
                                                    }} 
                                                />
                                            </Box>
                                            <Alert severity="success" sx={{ textAlign: 'left' }}>
                                                ¡Perfecto! Tu imagen está lista para ser actualizada.
                                            </Alert>
                                        </Box>
                                    ) : (
                                        <Box sx={{ py: 3 }}>
                                            <PhotoCameraIcon 
                                                sx={{ 
                                                    fontSize: 50, 
                                                    color: theme.palette.mode === 'dark'
                                                        ? theme.palette.grey[600]
                                                        : theme.palette.grey[400],
                                                    mb: 1 
                                                }} 
                                            />
                                            <Typography variant="body2" color="text.secondary">
                                                Selecciona una imagen para ver la vista previa
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ 
                px: 3, 
                py: 2, 
                backgroundColor: theme.palette.mode === 'dark' 
                    ? theme.palette.grey[900] 
                    : theme.palette.grey[50],
                borderTop: `1px solid ${theme.palette.divider}`
            }}>
                <Button 
                    onClick={handleClose}
                    sx={{ mr: 1 }}
                >
                    Cancelar
                </Button>
                <LoadingButton
                    loading={isUpdating}
                    onClick={handleUpdatePhoto}
                    variant="contained"
                    disabled={!preview}
                    startIcon={<CloudUploadIcon />}
                    sx={{ 
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 'bold'
                    }}
                >
                    {isUpdating ? 'Actualizando...' : 'Actualizar foto'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default PhotoUpdateModal;
